import { Component, Inject, OnInit, Optional, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { Subscription } from 'rxjs';
import { GroupEntityService } from '../../../groups/services/group-entity.service';
import { AffiliateGroupsDataService } from '../../../affiliate-groups/services/affiliate-groups-data.service';
import { AllAffiliatesDataService } from '../../../all-affiliates/services/all-affiliates-data.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';

@Component({
  selector: 'kt-promotion',
  templateUrl: './member-setting.component.html',
  styleUrls: ['./member-setting.component.scss']
})

export class MemberSettingComponent implements OnInit {
  form: FormGroup;
  private formSubscription: Subscription;
  private saveSubscription: Subscription;

  // Free Spin uses the full Eligible Type Setting (AIO-71840); PP keeps member-group-only.
  isFreeSpin = false;
  eligibleTypes = [
    { id: 1, name: 'Member' },
    { id: 2, name: 'Members under Affiliate' },
    { id: 3, name: 'Referral' },
    { id: 4, name: 'Telemarketer / Account Manager' },
  ];

  groupDropdownList = [];
  groupSelectedItems = [];
  // badgeShowLimit keeps the closed control to a single 44px row (the library default is
  // effectively unlimited, which overflowed .c-btn); the rest collapses into a "+N" counter.
  // enableCheckAll adds a Select All row so 20 groups aren't clicked one by one, and clearAll
  // adds an X to drop the whole selection. showCheckbox stays false everywhere in this app —
  // angular2-multiselect's default.theme.css isn't imported, so the checkbox would be invisible.
  groupDropdownSettings = {
    text: 'Please Select', enableFilterSelectAll: false, enableSearchFilter: true,
    classes: 'dropdown', primaryKey: 'name', labelKey: 'name', noDataLabel: '', showCheckbox: false, disabled: false,
    badgeShowLimit: 6, enableCheckAll: true, selectAllText: 'Select All', unSelectAllText: 'Unselect All', clearAll: true
  };

  affiliateGroupDropdownList = [];
  affiliateGroupSelectedItems = [];        // Members-under-Affiliate -> affiliate_group_ids
  affiliateGroupDropdownSettings = {
    text: 'Please Select', enableFilterSelectAll: false, enableSearchFilter: true,
    classes: 'dropdown', primaryKey: 'name', labelKey: 'name', noDataLabel: '', showCheckbox: false, disabled: false,
    badgeShowLimit: 6, enableCheckAll: true, selectAllText: 'Select All', unSelectAllText: 'Unselect All', clearAll: true
  };

  // Member mode "Affiliate List" (promotion_affiliate_group rows)
  affiliateListDropdownList = [];          // all affiliates for the per-row affiliate_id multiselect
  affiliateListSelectedItems: any[][] = []; // selected affiliates per row
  affiliateDropdownListSettings = {
    text: 'Please Select', enableFilterSelectAll: false, enableSearchFilter: true,
    classes: 'dropdown', primaryKey: 'username', labelKey: 'username', noDataLabel: '', showCheckbox: false, disabled: false,
    badgeShowLimit: 2, enableCheckAll: true, selectAllText: 'Select All', unSelectAllText: 'Unselect All', clearAll: true
  };
  // Affiliate List pagination (client-side, over the added rows)
  affPage = 1;
  affPageSize = 30;

  // Members-under-Affiliate: specific affiliates (affiliate_ids) list.
  // The "add" picker is a searchable single-select; it lives on its own FormGroup so the
  // staging value never leaks into the payload emitted from this.form.valueChanges.
  affiliateAddForm: FormGroup;
  affiliateToAddSelectedItems = [];
  availableAffiliateList = [];             // affiliates not yet added — kept as a stable array reference
  affiliateAddDropdownSettings = {
    singleSelection: true, text: 'Please Select', enableFilterSelectAll: false, enableSearchFilter: true,
    classes: 'dropdown', maxHeight: 200, primaryKey: 'id', labelKey: 'username', noDataLabel: '', showCheckbox: false, disabled: false
  };
  affListPage = 1;
  affListPageSize = 30;

  telemarketerDropdownList = [];
  telemarketerSelectedItems = [];
  normalAccountManagerDropdownList = [];
  normalAccountManagerSelectedItems = [];
  vipAccountManagerDropdownList = [];
  vipAccountManagerSelectedItems = [];
  boUserDropdownSettings = {
    text: 'Please Select', enableFilterSelectAll: false, enableSearchFilter: true,
    classes: 'dropdown', primaryKey: 'username', labelKey: 'username', noDataLabel: '', showCheckbox: false, disabled: false,
    badgeShowLimit: 3, enableCheckAll: true, selectAllText: 'Select All', unSelectAllText: 'Unselect All', clearAll: true
  };

  // Dropdown loading flags — each shows the spinner and disables its control until the list arrives,
  // so an empty dropdown is never mistaken for "no options". The three BO-user dropdowns share one
  // settings object (and load together), so they share a flag driven by a pending-request counter.
  memberGroupLoading = false;
  affiliateGroupLoading = false;
  affiliateLoading = false;
  boUserLoading = false;
  private boUserPending = 0;

  // Inline feedback banner for transient dropdown-load failures (mirrors provider-blacklist).
  feedbackMsg = '';

  constructor(
    @Optional() @Inject('initialData') private initialData: any,
    private eventEmitterService: EventEmitterService,
    private groupService: GroupEntityService,
    private affiliateGroupsDataService: AffiliateGroupsDataService,
    private allAffiliatesDataService: AllAffiliatesDataService,
    private dropdownHttpService: DropdownHttpService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initialData = initialData;
  }

  ngOnInit() {
    this.isFreeSpin = this.initialData?.promotion_type == 'Free Spin';
    this.formInit();
    this.eventEmitterService.onMemberSettingData(this.form.value);
    this.formSubscription = this.form.valueChanges.subscribe(values => {
      this.eventEmitterService.onMemberSettingData(values);
    });
    this.setMemberGroup();

    if (this.isFreeSpin) {
      this.setAffiliateGroup();
      this.setAffiliates();
      this.setTelemarketers();
      this.setNormalAccountManagers();
      this.setVipAccountManagers();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) this.formSubscription.unsubscribe();
    if (this.saveSubscription) this.saveSubscription.unsubscribe();
  }

  private formInit() {
    let member_group_ids = this.initialData?.member_group_ids || [];

    this.form = new FormGroup({
      member_group_ids: new FormControl(member_group_ids),
    });

    this.affiliateAddForm = new FormGroup({
      affiliate_to_add: new FormControl(null),
    });

    if (this.isFreeSpin) {
      this.form.addControl('eligible_types', new FormControl(this.initialData?.eligible_types ?? 1));
      this.form.addControl('affiliate_group_ids', new FormControl(this.initialData?.affiliate_group_ids || []));
      this.form.addControl('affiliate_ids', new FormControl(this.initialData?.affiliate_ids || []));
      this.form.addControl('telemarketer_ids', new FormControl(this.initialData?.telemarketer_ids || []));
      this.form.addControl('normal_account_manager_ids', new FormControl(this.initialData?.normal_account_manager_ids || []));
      this.form.addControl('vip_account_manager_ids', new FormControl(this.initialData?.vip_account_manager_ids || []));
      this.form.addControl('visible_by_affiliate', new FormControl(this.initialData?.visible_by_affiliate ? 1 : 0));

      // Member mode "Affiliate List" — a FormArray of { affiliate_group_id, visibility, affiliate_id }
      const rows = (this.initialData?.promotion_affiliate_group || []).map((g: any) => this.buildAffRow(g));
      this.form.addControl('promotion_affiliate_group', new FormArray(rows));
      this.affiliateListSelectedItems = (this.initialData?.promotion_affiliate_group || []).map(() => []);
    }
  }

  private buildAffRow(g?: any): FormGroup {
    return new FormGroup({
      affiliate_group_id: new FormControl(g?.affiliate_group_id ?? null, [Validators.required]),
      visibility: new FormControl(g?.visibility ?? 1, [Validators.required]),
      affiliate_id: new FormControl(g?.affiliate_id ?? []),
    });
  }

  get promoAffGroups(): FormArray {
    return this.form.get('promotion_affiliate_group') as FormArray;
  }

  addAffRow() {
    this.promoAffGroups.push(this.buildAffRow());
    this.affiliateListSelectedItems.push([]);
    this.affPage = this.affTotalPages; // jump to the page holding the new row
  }

  removeAffRow(i: number) {
    this.promoAffGroups.removeAt(i);
    this.affiliateListSelectedItems.splice(i, 1);
    if (this.affPage > this.affTotalPages) this.affPage = this.affTotalPages;
  }

  get affTotalPages(): number {
    return Math.max(1, Math.ceil(this.promoAffGroups.length / this.affPageSize));
  }
  affShowingFrom(): number {
    return this.promoAffGroups.length === 0 ? 0 : (this.affPage - 1) * this.affPageSize + 1;
  }
  affShowingTo(): number {
    return Math.min(this.affPage * this.affPageSize, this.promoAffGroups.length);
  }
  affOnPageSize(size: number) {
    this.affPageSize = +size || 30;
    this.affPage = 1;
  }

  // ----- Members-under-Affiliate: affiliate_ids list -----
  // Recomputed on add/remove/load instead of called from the template — the searchable dropdown
  // takes the list as an @Input, so it must not get a fresh array on every change-detection pass.
  private refreshAvailableAffiliates() {
    const ids = this.form.value.affiliate_ids || [];
    this.availableAffiliateList = this.affiliateListDropdownList.filter((a: any) => !ids.includes(a.id));
  }
  selectedAffiliates(): any[] {
    const ids = this.form.value.affiliate_ids || [];
    return this.affiliateListDropdownList.filter((a: any) => ids.includes(a.id));
  }
  addAffiliateId() {
    const affiliateToAdd = this.affiliateAddForm.value.affiliate_to_add;
    if (affiliateToAdd == null) return;
    const ids = this.form.value.affiliate_ids || [];
    if (!ids.includes(affiliateToAdd)) {
      this.form.patchValue({ affiliate_ids: [...ids, affiliateToAdd] });
    }
    this.affiliateAddForm.patchValue({ affiliate_to_add: null });
    this.affiliateToAddSelectedItems = [];
    this.refreshAvailableAffiliates();
    this.affListPage = this.affListTotalPages;
  }
  removeAffiliateId(id: any) {
    this.form.patchValue({ affiliate_ids: (this.form.value.affiliate_ids || []).filter((x: any) => x !== id) });
    this.refreshAvailableAffiliates();
    if (this.affListPage > this.affListTotalPages) this.affListPage = this.affListTotalPages;
  }
  get affListTotalPages(): number {
    return Math.max(1, Math.ceil(this.selectedAffiliates().length / this.affListPageSize));
  }
  affListShowingFrom(): number {
    return this.selectedAffiliates().length === 0 ? 0 : (this.affListPage - 1) * this.affListPageSize + 1;
  }
  affListShowingTo(): number {
    return Math.min(this.affListPage * this.affListPageSize, this.selectedAffiliates().length);
  }
  affListOnPageSize(size: number) {
    this.affListPageSize = +size || 30;
    this.affListPage = 1;
  }

  onRowVisibilityChange(i: number) {
    const row = this.promoAffGroups.at(i);
    if (+row.value.visibility === 1) {
      row.patchValue({ affiliate_id: [] });
      this.affiliateListSelectedItems[i] = [];
    }
  }

  selectEligibleType(id: number) {
    this.form.patchValue({ eligible_types: id });
    this.onEligibleTypeChange();
  }

  // Clear the other-mode selections so stale data isn't sent.
  onEligibleTypeChange() {
    const type = +this.form.value.eligible_types;
    if (type !== 1 && type !== 3) { this.form.patchValue({ member_group_ids: [] }); this.groupSelectedItems = []; }
    if (type !== 2) {
      this.form.patchValue({ affiliate_group_ids: [], affiliate_ids: [] });
      this.affiliateGroupSelectedItems = [];
      this.affiliateAddForm.patchValue({ affiliate_to_add: null });
      this.affiliateToAddSelectedItems = [];
      this.refreshAvailableAffiliates();
    }
    if (type !== 1) {
      this.form.patchValue({ visible_by_affiliate: 0 });
      this.promoAffGroups.clear();
      this.affiliateListSelectedItems = [];
    }
    if (type !== 4) {
      this.form.patchValue({ telemarketer_ids: [], normal_account_manager_ids: [], vip_account_manager_ids: [] });
      this.telemarketerSelectedItems = [];
      this.normalAccountManagerSelectedItems = [];
      this.vipAccountManagerSelectedItems = [];
    }
    this.cdr.detectChanges();
  }

  onChangeToggle(event: any, controlName: string) {
    this.form.patchValue({ [controlName]: event.target.checked ? 1 : 0 });
  }

  private setMemberGroup() {
    if (this.groupDropdownList.length === 0) {
      this.memberGroupLoading = true;
      this.groupDropdownSettings = { ...this.groupDropdownSettings, disabled: true };
      this.groupService.getWithQuery(`?status=1&paginate=false`).subscribe({
        next: res => {
          this.groupDropdownList = res;
          (this.initialData?.member_group_ids || []).forEach((value: any) => {
            const item = res.find(v => v.id === value);
            if (item) this.groupSelectedItems.push(item);
          });
          this.stopMemberGroupLoading();
        },
        error: () => this.stopMemberGroupLoading(),
      });
    }
  }

  private stopMemberGroupLoading() {
    this.memberGroupLoading = false;
    this.groupDropdownSettings = { ...this.groupDropdownSettings, disabled: false };
    this.cdr.detectChanges();
  }

  private setAffiliateGroup() {
    this.affiliateGroupLoading = true;
    this.affiliateGroupDropdownSettings = { ...this.affiliateGroupDropdownSettings, disabled: true };
    this.affiliateGroupsDataService.getWithQuery(`?status=1&paginate=false&dropdown=true`).subscribe({
      next: (res: any[]) => {
        this.affiliateGroupDropdownList = res || [];
        (this.initialData?.affiliate_group_ids || []).forEach((id: any) => {
          const item = this.affiliateGroupDropdownList.find((v: any) => v.id === id);
          if (item) this.affiliateGroupSelectedItems.push(item);
        });
        this.stopAffiliateGroupLoading();
      },
      error: (err: any) => {
        this.affiliateGroupDropdownList = [];
        this.flash(this.apiError(err, 'Failed to load affiliate groups.'));
        this.stopAffiliateGroupLoading();
      },
    });
  }

  private stopAffiliateGroupLoading() {
    this.affiliateGroupLoading = false;
    this.affiliateGroupDropdownSettings = { ...this.affiliateGroupDropdownSettings, disabled: false };
    this.cdr.detectChanges();
  }

  private setAffiliates() {
    this.affiliateLoading = true;
    this.affiliateDropdownListSettings = { ...this.affiliateDropdownListSettings, disabled: true };
    this.affiliateAddDropdownSettings = { ...this.affiliateAddDropdownSettings, disabled: true };
    this.allAffiliatesDataService.getWithQuery(`?status=1&paginate=false&dropdown=true`).subscribe({
      next: (res: any[]) => {
        this.affiliateListDropdownList = res || [];
        // preselect affiliates for existing rows
        (this.initialData?.promotion_affiliate_group || []).forEach((g: any, i: number) => {
          const ids = Array.isArray(g.affiliate_id) ? g.affiliate_id : [];
          this.affiliateListSelectedItems[i] = this.affiliateListDropdownList.filter((a: any) => ids.includes(a.id));
        });
        this.refreshAvailableAffiliates();
        this.stopAffiliateLoading();
      },
      error: (err: any) => {
        this.affiliateListDropdownList = [];
        this.refreshAvailableAffiliates();
        this.flash(this.apiError(err, 'Failed to load affiliates.'));
        this.stopAffiliateLoading();
      },
    });
  }

  private stopAffiliateLoading() {
    this.affiliateLoading = false;
    this.affiliateDropdownListSettings = { ...this.affiliateDropdownListSettings, disabled: false };
    this.affiliateAddDropdownSettings = { ...this.affiliateAddDropdownSettings, disabled: false };
    this.cdr.detectChanges();
  }

  private setTelemarketers() {
    this.startBoUserLoading();
    this.dropdownHttpService.telemarketer.subscribe({
      next: (res: any[]) => {
        this.telemarketerDropdownList = res || [];
        this.telemarketerSelectedItems = this.matchSelected(this.telemarketerDropdownList, this.initialData?.telemarketer_ids);
        this.stopBoUserLoading();
      },
      error: (err: any) => {
        this.telemarketerDropdownList = [];
        this.flash(this.apiError(err, 'Failed to load telemarketers.'));
        this.stopBoUserLoading();
      },
    });
  }

  private setNormalAccountManagers() {
    this.startBoUserLoading();
    this.dropdownHttpService.normalAccountManager.subscribe({
      next: (res: any[]) => {
        this.normalAccountManagerDropdownList = res || [];
        this.normalAccountManagerSelectedItems = this.matchSelected(this.normalAccountManagerDropdownList, this.initialData?.normal_account_manager_ids);
        this.stopBoUserLoading();
      },
      error: (err: any) => {
        this.normalAccountManagerDropdownList = [];
        this.flash(this.apiError(err, 'Failed to load account managers.'));
        this.stopBoUserLoading();
      },
    });
  }

  private setVipAccountManagers() {
    this.startBoUserLoading();
    this.dropdownHttpService.vipAccountManager.subscribe({
      next: (res: any[]) => {
        this.vipAccountManagerDropdownList = res || [];
        this.vipAccountManagerSelectedItems = this.matchSelected(this.vipAccountManagerDropdownList, this.initialData?.vip_account_manager_ids);
        this.stopBoUserLoading();
      },
      error: (err: any) => {
        this.vipAccountManagerDropdownList = [];
        this.flash(this.apiError(err, 'Failed to load VIP account managers.'));
        this.stopBoUserLoading();
      },
    });
  }

  private startBoUserLoading() {
    this.boUserPending++;
    this.boUserLoading = true;
    this.boUserDropdownSettings = { ...this.boUserDropdownSettings, disabled: true };
  }

  private stopBoUserLoading() {
    this.boUserPending = Math.max(0, this.boUserPending - 1);
    if (this.boUserPending === 0) {
      this.boUserLoading = false;
      this.boUserDropdownSettings = { ...this.boUserDropdownSettings, disabled: false };
    }
    this.cdr.detectChanges();
  }

  private flash(msg: string) {
    this.feedbackMsg = msg;
  }

  private apiError(err: any, fallback: string): string {
    const m = err?.error?.message ?? err?.message;
    if (Array.isArray(m)) return m.join(' ');
    return typeof m === 'string' ? m : fallback;
  }

  private matchSelected(list: any[], ids: any[]): any[] {
    if (!Array.isArray(ids)) return [];
    const norm = ids.map((x: any) => (x && typeof x === 'object') ? x.id : x);
    return list.filter((v: any) => norm.includes(v.id));
  }
}
